import type { MediaAccessDecision } from "@topzle/api-client";
import { CheckCircle2, KeyRound, LoaderCircle, LogIn, Phone, ShieldCheck, X } from "lucide-react";
import { useCallback, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";
import { signInPath } from "../authReturn";
import { supabase } from "../supabase";
import { useVisitorContext } from "../visitorContext";
import { OtpSafeInput, type OtpSafeState, otpUnlockDelay } from "./OtpSafeInput";
import { TurnstileWidget, type TurnstileWidgetHandle } from "./TurnstileWidget";

export function CallAccessDialog({
  decision,
  roomCode,
  onClose,
  onResolved,
}: {
  decision: MediaAccessDecision;
  roomCode: string;
  onClose: () => void;
  onResolved: () => Promise<void>;
}) {
  const visitor = useVisitorContext();
  const challengeRef = useRef<TurnstileWidgetHandle>(null);
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [otpState, setOtpState] = useState<OtpSafeState>("idle");
  const [step, setStep] = useState<"phone" | "code" | "complete">("phone");
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [message, setMessage] = useState(decision.message ?? "Calls are unavailable.");
  const receiveChallenge = useCallback((token: string | null) => setTurnstileToken(token), []);
  const returnTo = `/join/${encodeURIComponent(roomCode)}?autojoin=1`;

  const normalizedPhone = () => {
    const entered = phone.trim().replace(/[\s()-]/g, "");
    const digits = entered.replace(/\D/g, "");
    return entered.startsWith("+") ? entered : `${visitor.callingCode ?? ""}${digits}`;
  };

  const requestCode = async () => {
    const value = normalizedPhone();
    if (!/^\+[1-9]\d{7,14}$/.test(value))
      throw new Error("Enter a full mobile number, including its country code.");
    if (!turnstileToken) throw new Error("Complete the security check first.");
    const result = await api.requestPhoneOtp({ phone: value, turnstileToken });
    setPhone(value);
    setStep("code");
    setCode("");
    setOtpState("idle");
    setMessage(`We sent a six-digit code to ${result.destination}.`);
  };

  const verifyCode = async () => {
    if (!/^\d{6}$/.test(code)) throw new Error("Enter the six-digit code from the text message.");
    setOtpState("validating");
    await api.linkVerifiedPhone({ phone, code });
    if (supabase) await supabase.auth.refreshSession();
    setOtpState("success");
    await otpUnlockDelay();
    setStep("complete");
    setMessage("Phone verified. Voice and video are ready for this room.");
    await onResolved();
  };

  return (
    <div className="modal-backdrop call-access-backdrop">
      <button type="button" className="modal-dismiss" aria-label="Close" onClick={onClose} />
      <section
        className="call-access-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="call-access-title"
      >
        <button
          type="button"
          className="icon-button call-access-close"
          onClick={onClose}
          aria-label="Close"
        >
          <X />
        </button>
        <span className="call-access-icon">
          {step === "complete" ? (
            <CheckCircle2 />
          ) : decision.action === "sign_in" ? (
            <LogIn />
          ) : (
            <ShieldCheck />
          )}
        </span>
        <span className="eyebrow">VOICE &amp; VIDEO ACCESS</span>
        <h2 id="call-access-title">
          {decision.action === "sign_in"
            ? "Keep playing—sign in for calls"
            : decision.action === "verify_phone"
              ? "Verify your phone here"
              : "Calls are paused"}
        </h2>
        <p aria-live="polite">{message}</p>

        {decision.action === "sign_in" && (
          <div className="call-access-actions">
            <Link className="button button-primary wide" to={signInPath(returnTo)}>
              <LogIn /> Sign in or create account
            </Link>
            <small>
              Your account is created automatically if you are new, then this room reopens.
            </small>
            <button type="button" className="button button-ghost wide" onClick={onClose}>
              Keep playing anonymously
            </button>
          </div>
        )}

        {decision.action === "verify_phone" && step !== "complete" && (
          <form
            className="call-access-form"
            onSubmit={(event) => {
              event.preventDefault();
              setPending(true);
              setMessage("");
              void (step === "phone" ? requestCode() : verifyCode())
                .catch((error) => {
                  setMessage(error instanceof Error ? error.message : "Verification failed.");
                  if (step === "phone") challengeRef.current?.reset();
                  else setOtpState("error");
                })
                .finally(() => setPending(false));
            }}
          >
            {step === "phone" ? (
              <>
                <label>
                  <Phone /> Mobile number
                  <input
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    value={phone}
                    onChange={(event) => setPhone(event.target.value.slice(0, 24))}
                    placeholder={`${visitor.callingCode ?? "+91"} 98765 43210`}
                    disabled={pending}
                  />
                </label>
                <TurnstileWidget
                  ref={challengeRef}
                  action="phone_auth"
                  onToken={receiveChallenge}
                />
              </>
            ) : (
              <label htmlFor="call-access-phone-code">
                <KeyRound /> Verification code
                <OtpSafeInput
                  id="call-access-phone-code"
                  value={code}
                  onChange={(value) => {
                    setCode(value);
                    setOtpState("idle");
                  }}
                  state={otpState}
                  disabled={pending}
                />
              </label>
            )}
            <button
              className="button button-primary wide"
              type="submit"
              disabled={pending || (step === "phone" ? !turnstileToken : code.length !== 6)}
            >
              {pending ? (
                <LoaderCircle className="spin" />
              ) : step === "phone" ? (
                <Phone />
              ) : (
                <KeyRound />
              )}
              {pending
                ? "Please wait…"
                : step === "phone"
                  ? "Send verification code"
                  : "Verify & enable calls"}
            </button>
            {step === "code" && (
              <button
                type="button"
                className="button button-ghost wide"
                onClick={() => {
                  setStep("phone");
                  setOtpState("idle");
                }}
                disabled={pending}
              >
                Change number
              </button>
            )}
          </form>
        )}

        {step === "complete" && (
          <button type="button" className="button button-primary wide" onClick={onClose}>
            <CheckCircle2 /> Continue playing
          </button>
        )}
      </section>
    </div>
  );
}
